import { Outlet, useNavigate } from "@tanstack/react-router"
import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import DateDisplay from "design-system/molecules/DateDisplay"
import { Section } from "design-system/molecules/Section"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { CopyButton } from "design-system/organisms/chat/CopyButton"
import { features, type Features } from "lib/table"
import { Plus } from "lucide-react"
import { useTranslation } from "react-i18next"
import { ApiTokenAccessEnum, ApiTokensListResponseData } from "schema"
import { ApiTokenDropdown } from "./ApiTokenDropdown"

const columnHelper = createColumnHelper<Features, ApiTokensListResponseData[number]>()

export function ApiTokensTable({
  data,
  isPending,
}: {
  data: ApiTokensListResponseData | undefined
  isPending: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columns = columnHelper.columns([
    columnHelper.accessor("name", {
      header: t("settings.keysAndSecrets.apiTokens.table.name"),
    }),
    columnHelper.accessor("token", {
      header: t("settings.keysAndSecrets.apiTokens.table.token"),
      cell: (token) => (
        <div className="flex flex-row items-center gap-1">
          <span className="font-mono">{token.getValue()}</span>
          <CopyButton
            content={token.getValue()}
            copyMessage={t("settings.keysAndSecrets.apiTokens.table.copySuccess")}
            positioning={false}
          />
        </div>
      ),
      meta: {
        colSpan: 3,
      },
    }),
    columnHelper.accessor("access", {
      header: t("settings.keysAndSecrets.apiTokens.table.access"),
      cell: (access) => (
        <Badge variant={access.getValue() === ApiTokenAccessEnum.Read ? "secondary" : "outline"}>
          {t(
            access.getValue() === ApiTokenAccessEnum.Read
              ? "settings.keysAndSecrets.apiTokens.access.read"
              : "settings.keysAndSecrets.apiTokens.access.full",
          )}
        </Badge>
      ),
    }),
    columnHelper.accessor("createdAt", {
      header: t("settings.keysAndSecrets.apiTokens.table.createdAt"),
      cell: (createdAt) => <DateDisplay date={createdAt.getValue()} />,
      meta: {
        align: "right",
      },
    }),
    columnHelper.display({
      id: "id",
      cell: (props) => <ApiTokenDropdown id={props.row.original.id} />,
      meta: {
        align: "right",
      },
    }),
  ])

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderRows() {
    if (isPending || !data) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("settings.keysAndSecrets.apiTokens.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Section
      title={t("settings.keysAndSecrets.apiTokens.title")}
      description={t("settings.keysAndSecrets.apiTokens.description")}
      action={
        <Button onClick={() => navigate({ to: "/settings/keys-and-secrets/api-tokens/create" })}>
          <Plus size={18} />
          {t("settings.keysAndSecrets.apiTokens.addToken")}
        </Button>
      }
    >
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} {...header.column.columnDef.meta}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{renderRows()}</TableBody>
      </Table>
      <Outlet />
    </Section>
  )
}
